export enum ButtonType {
  GREEN = 'green',
  NEON_GREEN = 'neonGreen',
  GRAPHITE_GRAY = 'graphiteGray',
  GOLD_GRADIENT = 'goldGradient',
  BLUE = 'blue',
  GRAY = 'gray',
  DARK_GRAY = 'darkGray',
  LIGHT_GRAY = 'lightGray',
  BLUE_TRANSPARENT = 'blueTransparent',
  GREEN_TRANSPARENT = 'greenTransparent',
  PINK_TRANSPARENT = 'pinkTransparent',
  WHITE_TRANSPARENT = 'whiteTransparent',
  WHITE_TRANSPARENT_NO_BORDER = 'whiteTransparentNoBorder',
  TRANSPARENT = 'transparent',
}

interface Color {
  color: string;
  opacity: number;
}

interface Gradient {
  from: Color;
  to: Color;
}

interface ButtonTypeStyle {
  backgroundGradient: Gradient;
  borderGradient: Gradient;
  textColor: string;
}

interface ButtonTypeHoverStyle extends ButtonTypeStyle {
  isDarkGradient?: boolean;
}

export interface ButtonTypeStyles {
  default: ButtonTypeStyle;
  hover: ButtonTypeHoverStyle;
}

export const mapButtonTypesStyles: { [type: string]: ButtonTypeStyles } = {
  [ButtonType.GREEN]: {
    default: {
      backgroundGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B67D', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.3 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#3DBF1C', opacity: 1 },
        to: { color: '#1CC39B', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.3 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
      isDarkGradient: true,
    },
  },

  [ButtonType.NEON_GREEN]: {
    default: {
      backgroundGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B83E', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B83E', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#019132', opacity: 1 },
        to: { color: '#019132', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B83E', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.GOLD_GRADIENT]: {
    default: {
      backgroundGradient: {
        from: { color: '#FF7B00', opacity: 1 },
        to: { color: '#FFCB00', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#000000',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#FF7B00', opacity: 0.8 },
        to: { color: '#FFCB00', opacity: 0.8 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#000000',
      isDarkGradient: true,
    },
  },

  [ButtonType.BLUE]: {
    default: {
      backgroundGradient: {
        from: { color: '#514DDB', opacity: 1 },
        to: { color: '#02A7DB', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.3 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      isDarkGradient: true,
      backgroundGradient: {
        from: { color: '#514DDB', opacity: 1 },
        to: { color: '#02A7DB', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.3 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.GRAY]: {
    default: {
      backgroundGradient: {
        from: { color: '#1F202C', opacity: 1 },
        to: { color: '#1F202C', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#1F202C', opacity: 1 },
        to: { color: '#1F202C', opacity: 1 },
      },
      textColor: '#9A9CAC',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#1F202C', opacity: 1 },
        to: { color: '#1F202C', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#1F202C', opacity: 1 },
        to: { color: '#1F202C', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.DARK_GRAY]: {
    default: {
      backgroundGradient: {
        from: { color: '#0E111A', opacity: 1 },
        to: { color: '#0E111A', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#0E111A', opacity: 1 },
        to: { color: '#0E111A', opacity: 1 },
      },
      textColor: '#9A9CAC',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#0E111A', opacity: 1 },
        to: { color: '#0E111A', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#0E111A', opacity: 1 },
        to: { color: '#0E111A', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.GRAPHITE_GRAY]: {
    default: {
      backgroundGradient: {
        from: { color: '#4C4D56', opacity: 1 },
        to: { color: '#4C4D56', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#4C4D56', opacity: 1 },
        to: { color: '#4C4D56', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#3e3f45', opacity: 1 },
        to: { color: '#3e3f45', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#4C4D56', opacity: 1 },
        to: { color: '#4C4D56', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.LIGHT_GRAY]: {
    default: {
      backgroundGradient: {
        from: { color: '#323444', opacity: 1 },
        to: { color: '#323444', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#323444', opacity: 1 },
        to: { color: '#323444', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#323444', opacity: 1 },
        to: { color: '#323444', opacity: 1 },
      },
      borderGradient: {
        from: { color: '#323444', opacity: 1 },
        to: { color: '#323444', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.GREEN_TRANSPARENT]: {
    default: {
      backgroundGradient: {
        from: { color: '#00b83e', opacity: 0.2 },
        to: { color: '#00B87D', opacity: 0.2 },
      },
      borderGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B67D', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#00b83e', opacity: 0.4 },
        to: { color: '#00B87D', opacity: 0.4 },
      },
      borderGradient: {
        from: { color: '#00B83E', opacity: 1 },
        to: { color: '#00B67D', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.BLUE_TRANSPARENT]: {
    default: {
      backgroundGradient: {
        from: { color: '#514DDB', opacity: 0.2 },
        to: { color: '#02A7DB', opacity: 0.2 },
      },
      borderGradient: {
        from: { color: '#514DDB', opacity: 1 },
        to: { color: '#02A7DB', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#514DDB', opacity: 0.4 },
        to: { color: '#02A7DB', opacity: 0.4 },
      },
      borderGradient: {
        from: { color: '#514DDB', opacity: 1 },
        to: { color: '#02A7DB', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.PINK_TRANSPARENT]: {
    default: {
      backgroundGradient: {
        from: { color: '#B8167A', opacity: 0.2 },
        to: { color: '#D6385E', opacity: 0.2 },
      },
      borderGradient: {
        from: { color: '#B81D7A', opacity: 1 },
        to: { color: '#D6385E', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#B8167A', opacity: 0.4 },
        to: { color: '#D6385E', opacity: 0.4 },
      },
      borderGradient: {
        from: { color: '#B81D7A', opacity: 1 },
        to: { color: '#D6385E', opacity: 1 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.WHITE_TRANSPARENT]: {
    default: {
      backgroundGradient: {
        from: { color: '#FFFFFF', opacity: 0.2 },
        to: { color: '#FFFFFF', opacity: 0.2 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.1 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#FFFFFF', opacity: 0.4 },
        to: { color: '#FFFFFF', opacity: 0.4 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0.1 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.WHITE_TRANSPARENT_NO_BORDER]: {
    default: {
      backgroundGradient: {
        from: { color: '#FFFFFF', opacity: 0.2 },
        to: { color: '#FFFFFF', opacity: 0.2 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
    hover: {
      backgroundGradient: {
        from: { color: '#FFFFFF', opacity: 0.4 },
        to: { color: '#FFFFFF', opacity: 0.4 },
      },
      borderGradient: {
        from: { color: '#FFFFFF', opacity: 0 },
        to: { color: '#FFFFFF', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
  },

  [ButtonType.TRANSPARENT]: {
    default: {
      backgroundGradient: {
        from: { color: 'transparent', opacity: 0 },
        to: { color: 'transparent', opacity: 0 },
      },
      borderGradient: {
        from: { color: 'transparent', opacity: 0 },
        to: { color: 'transparent', opacity: 0 },
      },
      textColor: '#9A9CAC',
    },
    hover: {
      backgroundGradient: {
        from: { color: 'transparent', opacity: 0 },
        to: { color: 'transparent', opacity: 0 },
      },
      borderGradient: {
        from: { color: 'transparent', opacity: 0 },
        to: { color: 'transparent', opacity: 0 },
      },
      textColor: '#FFFFFF',
    },
  },
};
